// Editor de criterios de segmento (filtros de la lista de contactos y de los segmentos dinámicos).
import type { JSX } from "react";
import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Button, Field, Input, Select } from "../../../components/ui.tsx";
import { cleanCriteria, type Criteria, NO_STATUS, type Options } from "./shared.tsx";

interface Opt { value: string; label: string }

/** Selección múltiple: botones conmutables si hay pocas opciones; si no, desplegable + chips. */
function MultiPick({ options, value, onChange, placeholder }: { options: Opt[]; value: string[]; onChange: (v: string[]) => void; placeholder: string }): JSX.Element {
  const toggle = (v: string): void => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  if (options.length <= 8) {
    return (
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => {
          const on = value.includes(o.value);
          return (
            <button key={o.value} type="button" aria-pressed={on} onClick={() => toggle(o.value)}
              className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${on ? "bg-primary-soft border-primary text-primary dark:text-ink" : "border-line text-ink-secondary hover:bg-surface-hover"}`}>
              {o.label}
            </button>
          );
        })}
      </div>
    );
  }
  const label = (v: string): string => options.find((o) => o.value === v)?.label ?? v;
  return (
    <div className="space-y-1.5">
      <Select value="" onChange={(e) => { if (e.target.value) toggle(e.target.value); }} aria-label={placeholder}>
        <option value="">{placeholder}</option>
        {options.filter((o) => !value.includes(o.value)).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </Select>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {value.map((v) => (
            <span key={v} className="badge bg-primary-soft text-primary dark:text-ink gap-1">
              {label(v)}
              <button type="button" onClick={() => toggle(v)} aria-label={`Quitar ${label(v)}`}><X size={12} /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export function FilterPanel(
  { value, options, onApply, onCancel, applyLabel = "Aplicar filtros" }:
  { value: Criteria; options: Options | undefined; onApply: (c: Criteria) => void; onCancel?: () => void; applyLabel?: string },
): JSX.Element {
  const [d, setD] = useState<Criteria>(value);
  useEffect(() => setD(value), [value]);
  const set = <K extends keyof Criteria>(k: K, v: Criteria[K]): void => setD((p) => ({ ...p, [k]: v }));
  const num = (s: string): number | undefined => (s.trim() === "" || Number.isNaN(Number(s)) ? undefined : Math.trunc(Number(s)));

  const tagOpts = (options?.tags ?? []).map((t) => ({ value: t, label: t }));
  const custom = d.custom ?? [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <Field label="Estado">
          <MultiPick placeholder="Añadir estado…" value={d.statuses ?? []} onChange={(v) => set("statuses", v)}
            options={[...(options?.statuses ?? []).map((s) => ({ value: s, label: s })), { value: NO_STATUS, label: "Sin estado" }]} />
        </Field>
        <Field label="Etiquetas">
          <MultiPick placeholder="Añadir etiqueta…" value={d.tags ?? []} onChange={(v) => set("tags", v)} options={tagOpts} />
          {(d.tags?.length ?? 0) > 1 && (
            <div className="mt-1.5 flex gap-3 text-xs text-ink-secondary">
              <label className="flex items-center gap-1"><input type="radio" checked={d.tagsMode !== "all"} onChange={() => set("tagsMode", "any")} /> Alguna</label>
              <label className="flex items-center gap-1"><input type="radio" checked={d.tagsMode === "all"} onChange={() => set("tagsMode", "all")} /> Todas</label>
            </div>
          )}
        </Field>
        <Field label="Excluir etiquetas">
          <MultiPick placeholder="Excluir etiqueta…" value={d.excludeTags ?? []} onChange={(v) => set("excludeTags", v)} options={tagOpts} />
        </Field>
        <Field label="Puntaje">
          <div className="flex items-center gap-2">
            <Input type="number" placeholder="Mín." value={d.scoreMin ?? ""} onChange={(e) => set("scoreMin", num(e.target.value))} aria-label="Puntaje mínimo" />
            <span className="text-ink-muted">–</span>
            <Input type="number" placeholder="Máx." value={d.scoreMax ?? ""} onChange={(e) => set("scoreMax", num(e.target.value))} aria-label="Puntaje máximo" />
          </div>
        </Field>
        <Field label="Propietario">
          <MultiPick placeholder="Añadir propietario…" value={d.ownerIds ?? []} onChange={(v) => set("ownerIds", v)}
            options={(options?.owners ?? []).map((o) => ({ value: o.id, label: o.name }))} />
        </Field>
        <Field label="Empresa (contiene)">
          <Input value={d.accountQ ?? ""} maxLength={120} onChange={(e) => set("accountQ", e.target.value)} placeholder="Nombre de la cuenta" />
        </Field>
        <Field label="Creado entre">
          <div className="flex items-center gap-2">
            <Input type="date" value={d.createdFrom ?? ""} onChange={(e) => set("createdFrom", e.target.value || undefined)} aria-label="Creado desde" />
            <span className="text-ink-muted">–</span>
            <Input type="date" value={d.createdTo ?? ""} onChange={(e) => set("createdTo", e.target.value || undefined)} aria-label="Creado hasta" />
          </div>
        </Field>
        <Field label="País">
          <MultiPick placeholder="País" value={d.countries ?? []} onChange={(v) => set("countries", v as Array<"CO" | "PE">)}
            options={[{ value: "CO", label: "Colombia" }, { value: "PE", label: "Perú" }]} />
        </Field>
        <Field label="Email">
          <Select value={d.hasEmail == null ? "" : d.hasEmail ? "1" : "0"} onChange={(e) => set("hasEmail", e.target.value === "" ? undefined : e.target.value === "1")}>
            <option value="">Indiferente</option>
            <option value="1">Con email</option>
            <option value="0">Sin email</option>
          </Select>
        </Field>
        <Field label="Origen (lead source)">
          <MultiPick placeholder="Origen" value={d.leadSources ?? []} onChange={(v) => set("leadSources", v)}
            options={(options?.leadSources ?? []).map((s) => ({ value: s, label: s }))} />
        </Field>
        <Field label="Actividad">
          <label className="flex items-center gap-2 text-sm text-ink-secondary mt-2">
            <input type="checkbox" checked={Boolean(d.neverContacted)} onChange={(e) => set("neverContacted", e.target.checked || undefined)} />
            Nunca contactados
          </label>
        </Field>
      </div>

      <Field label="Campos personalizados" hint="Igual a (sin distinguir mayúsculas). Máximo 5.">
        <div className="space-y-2">
          {custom.map((cf, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2">
              <Select className="w-auto min-w-48" value={cf.key} aria-label="Campo"
                onChange={(e) => set("custom", custom.map((x, j) => (j === i ? { ...x, key: e.target.value } : x)))}>
                {[...new Set([cf.key, ...(options?.customKeys ?? [])])].filter(Boolean).map((k) => <option key={k} value={k}>{k}</option>)}
              </Select>
              <span className="text-ink-muted text-sm">=</span>
              <Input className="w-auto flex-1 min-w-40" value={cf.value} maxLength={200} aria-label="Valor"
                onChange={(e) => set("custom", custom.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} />
              <Button small variant="ghost" onClick={() => set("custom", custom.filter((_, j) => j !== i))} aria-label="Quitar campo"><X size={14} /></Button>
            </div>
          ))}
          {custom.length < 5 && (options?.customKeys.length ?? 0) > 0 && (
            <Button small onClick={() => set("custom", [...custom, { key: options!.customKeys[0]!, value: "" }])}>+ Campo</Button>
          )}
        </div>
      </Field>

      <div className="flex flex-wrap justify-end gap-2 pt-2 border-t border-line">
        {onCancel && <Button onClick={onCancel}>Cancelar</Button>}
        <Button onClick={() => setD({})}>Limpiar</Button>
        <Button variant="primary" onClick={() => onApply(cleanCriteria({ ...d, custom: custom.filter((c) => c.key && c.value.trim()) }))}>{applyLabel}</Button>
      </div>
    </div>
  );
}
